import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { detectUserLanguage } from '../utils/languageDetection';
import debug from '../utils/debug';

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (lang: string) => void;
  supportedLanguages: { code: string; name: string; flag: string }[];
}

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'bn', name: 'বাংলা', flag: '🇧🇩' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ur', name: 'اردو', flag: '🇵🇰' }
];

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');

  // Listen for language changes from i18n
  useEffect(() => {
    // Update our state when i18n language changes
    const handleLanguageChanged = (lng: string) => {
      debug.log(`Language changed in i18n to: ${lng}`);
      setCurrentLanguage(lng);
    };

    // Add the listener
    i18n.on('languageChanged', handleLanguageChanged);

    // Initial sync with i18n
    if (i18n.language && i18n.language !== currentLanguage) {
      debug.log(`Syncing with current i18n language: ${i18n.language}`);
      setCurrentLanguage(i18n.language);
    }

    // Cleanup
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n, currentLanguage]);
  
  // Listen for the custom languageLoaded event
  useEffect(() => {
    const handleLanguageLoaded = () => {
      debug.log('Language loaded event received, updating current language');
      if (i18n.language && i18n.language !== currentLanguage) {
        debug.log(`Updating language from ${currentLanguage} to ${i18n.language}`);
        setCurrentLanguage(i18n.language);
      }
    };
    
    window.addEventListener('languageLoaded', handleLanguageLoaded);
    
    return () => {
      window.removeEventListener('languageLoaded', handleLanguageLoaded);
    };
  }, [i18n, currentLanguage]);

  const changeLanguage = async (lang: string) => {
    try {
      console.log(`[LanguageContext] Starting language change to ${lang}`);
      debug.log(`Changing language to ${lang} in LanguageContext`);

      // Log current state before change
      console.log(`[LanguageContext] Current i18n language before change:`, i18n.language);
      console.log(`[LanguageContext] Target language has resource bundle:`, i18n.hasResourceBundle(lang, 'translation'));

      // First update the local state
      setCurrentLanguage(lang);

      // Save to localStorage
      localStorage.setItem('preferredLanguage', lang);

      // Then change the i18n language
      console.log(`[LanguageContext] Calling i18n.changeLanguage(${lang})`);
      await i18n.changeLanguage(lang);

      // Log final state
      console.log(`[LanguageContext] Language change completed. New i18n language:`, i18n.language);
      console.log(`[LanguageContext] Resource bundle for ${lang} exists:`, i18n.hasResourceBundle(lang, 'translation'));

      if (lang === 'zh') {
        console.log(`[LanguageContext] Chinese resources:`, i18n.getResourceBundle('zh', 'translation'));
      }

      debug.log(`Language changed successfully to ${lang}`);
    } catch (error) {
      console.error(`[LanguageContext] Failed to change language to ${lang}:`, error);
      debug.error('Failed to change language:', error);
    }
  };

  const value = {
    currentLanguage,
    changeLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}